// TIP-493 (MV Stage 4b) — display pricing for the "Buy a Web Profile" checkout.
//
// Amounts are in AED ("Dh") and mirror the Stripe AED prices the backend charges
// (the PAYG web profile + the recurring membership). Keep them in sync with the
// minted Stripe prices.
//
// TIP-646 — these constants are now strictly EX-VAT. Tax is Stripe's to compute
// (automatic_tax, keyed off the billing-address country), and no billing country
// exists on the plan-select step — so the pre-checkout summary shows the net
// amount and says VAT is added at checkout. Once the billing-address step has a
// quote back from create_payment, `applyQuotedTax` overlays Stripe's real
// numbers. The old flat 5% here charged a Norway buyer's screen for VAT they
// would never be billed.

export const CURRENCY = "Dh";
export const MEMBER_DISCOUNT_RATE = 0.1; // 10% off the web profile for members
export const PROFILE_WINDOW_DAYS = 30;

export const WEB_PROFILE_STANDARD = 99; // Dh 99 / 30 days, one-time
export const WEB_PROFILE_MEMBER = Number(
  (WEB_PROFILE_STANDARD * (1 - MEMBER_DISCOUNT_RATE)).toFixed(2)
); // 89.10
export const WEB_PROFILE_SAVINGS = Number(
  (WEB_PROFILE_STANDARD - WEB_PROFILE_MEMBER).toFixed(2)
); // 9.90

export const MEMBERSHIP_ANNUAL = 999; // Dh 999 / year (two months free)
// TIP-526 — must mirror the Stripe AED price (STRIPE_PRICE_ID_MEMBERSHIP_MONTHLY
// = AED 99.00); a 99.90 here made the displayed total differ from the charge.
export const MEMBERSHIP_MONTHLY = 99; // Dh 99 / month

export const PLAN_YEARLY = "yearly";
export const PLAN_MONTHLY = "monthly";

export const formatAed = (n) =>
  `${CURRENCY} ${Number(n).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const membershipPrice = (plan) =>
  plan === PLAN_YEARLY ? MEMBERSHIP_ANNUAL : MEMBERSHIP_MONTHLY;

// TIP-526 — compute in integer fils so the member rate doesn't drift on float
// addition (999 + 89.10 must land on 1088.10, not 1088.0999…).
const exVat = (lines, subtotal, renewalNote = null) => {
  const subtotalFils = Math.round(subtotal * 100);
  return {
    lines,
    subtotal: subtotalFils / 100,
    // No billing country yet, so no VAT figure can honestly be shown.
    vat: null,
    total: subtotalFils / 100,
    quoted: false,
    renewalNote,
  };
};

// Right-hand summary line items + total for the chosen option.
//   mode 'payg'   → standard one-time web profile only.
//   mode 'bundle' → membership (plan) + web profile at the member rate.
//   mode 'member' → an existing member buying at the member rate (no membership line).
export const buildWebProfileSummary = ({ mode, plan = PLAN_YEARLY }) => {
  const profileLabel = `Web Profile · ${PROFILE_WINDOW_DAYS} days`;

  if (mode === "bundle") {
    const membership = membershipPrice(plan);
    return exVat(
      [
        {
          key: "membership",
          label: plan === PLAN_YEARLY ? "Annual Membership" : "Monthly Membership",
          value: membership,
        },
        {
          key: "profile",
          label: profileLabel,
          sublabel: "Member rate",
          value: WEB_PROFILE_MEMBER,
          strike: WEB_PROFILE_STANDARD,
        },
        {
          key: "savings",
          label: "Member savings",
          value: -WEB_PROFILE_SAVINGS,
          savings: true,
        },
      ],
      membership + WEB_PROFILE_MEMBER,
      plan === PLAN_YEARLY
        ? "Membership renews annually. Cancel any time at the end of the term."
        : "Membership renews monthly. Cancel any time at the end of the term."
    );
  }

  if (mode === "member") {
    return exVat(
      [
        {
          key: "profile",
          label: profileLabel,
          sublabel: "Member rate",
          value: WEB_PROFILE_MEMBER,
          strike: WEB_PROFILE_STANDARD,
        },
      ],
      WEB_PROFILE_MEMBER
    );
  }

  // payg (default)
  return exVat(
    [{ key: "profile", label: profileLabel, value: WEB_PROFILE_STANDARD }],
    WEB_PROFILE_STANDARD
  );
};

// TIP-646 — overlay Stripe's authoritative, country-aware figures onto an
// ex-VAT summary. `extraDetails` is create_payment's `extra_details` block,
// whose amounts are minor units (fils), computed by automatic_tax against the
// selected billing address. A UAE address yields 5%; anywhere else yields 0.
//
// Returns the summary untouched (and still `quoted: false`) when the quote is
// missing or incomplete: the screen must fall back to the honest "VAT excluded"
// view rather than render a number Stripe has not confirmed.
export const applyQuotedTax = (summary, extraDetails) => {
  const fils = (v) => (Number.isFinite(v) ? v : null);
  const subtotal = fils(extraDetails?.subtotal);
  const tax = fils(extraDetails?.tax);
  const total = fils(extraDetails?.total);

  if (subtotal === null || tax === null || total === null) return summary;

  return {
    ...summary,
    subtotal: subtotal / 100,
    vat: tax / 100,
    total: total / 100,
    quoted: true,
  };
};
